import { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/core/components/ui/tooltip';
import { Toaster } from '@/core/components/ui/toaster';
import { Toaster as Sonner } from '@/core/components/ui/sonner';
import { ServicesProvider } from '@/core/di';
import { DebugToolbar } from '@/core/components/DebugToolbar';
import { useUIPreferencesStore } from '@/core/stores/ui-preferences-store';
import { appConfig } from '@/core/config';

const queryClient = new QueryClient();

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders - Provider Wrapper Component
 * Wraps app with all necessary providers
 */
export const AppProviders = ({ children }: AppProvidersProps) => {
  // Check if debug toolbar is actually visible (feature flag AND user preference)
  const runtimeConfig = appConfig.getRuntimeConfig();
  const isDebugModeEnabled = runtimeConfig?.features?.debugMode ?? !appConfig.isProduction();
  const { isDebugToolbarVisible } = useUIPreferencesStore();
  const isDebugToolbarActuallyVisible = isDebugModeEnabled && isDebugToolbarVisible;

  return (
    <ServicesProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <DebugToolbar />
            <div className={isDebugToolbarActuallyVisible ? 'pt-8' : ''}>
              {children}
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ServicesProvider>
  );
};
